import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

/**
 * NoVideo — CleverParking "no video signal" empty state.
 *
 * A polished placeholder shown in the camera panel when there is no stream,
 * the stream errors, or no frame arrives within a short grace period (see
 * Img / VideoCap). Replaces the endless loading spinner with an intentional,
 * on-brand empty state: a muted "video off" glyph plus a short caption.
 *
 * Sits on the existing dark camera panel (#10151a). Fills its (relatively
 * positioned) parent and centers its content, so it works both as an overlay
 * over a stalled <img> and as standalone content inside VideoCap.
 */

const Wrap = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 16px;
  box-sizing: border-box;
  border-radius: 10px;
  background: #10151a;
  text-align: center;
  user-select: none;
  pointer-events: none;

  svg {
    display: block;
  }

  .nv-title {
    color: #aab2b8;
    font-size: 15px;
    font-weight: 500;
    letter-spacing: 0.2px;
    line-height: 1.2;
  }
  .nv-sub {
    color: #7a838a;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.2;
  }
`;

/**
 * @param {object} props
 * @param {string} [props.title]   primary caption (default: "Нет видеосигнала")
 * @param {string} [props.sub]     secondary caption (default: "Камера недоступна")
 * @param {number} [props.size]    icon size in px (default: 46)
 * @param {object} [props.style]   extra styles for the wrapper
 */
export const NoVideo = (props) => {
  const { t } = useTranslation();
  const { title = t('dock.Нет видеосигнала'), sub = t('dock.Камера недоступна'), size = 46, style } = props;

  return (
    <Wrap style={style} className="no-video">
      <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden="true">
        {/* camera body */}
        <rect x="4" y="15" width="27" height="18" rx="4" stroke="#7a838a" strokeWidth="2.2" strokeLinejoin="round" />
        {/* lens wedge */}
        <path d="M31 22.5 L43 16 v16 L31 25.5 Z" stroke="#7a838a" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
        {/* diagonal slash — solid CP grey, with a dark backing line so it reads as a clean cut */}
        <line x1="8" y1="9" x2="41" y2="41" stroke="#10151a" strokeWidth="5" strokeLinecap="round" />
        <line x1="8" y1="9" x2="41" y2="41" stroke="#7a838a" strokeWidth="2.4" strokeLinecap="round" />
      </svg>
      {title && <div className="nv-title">{title}</div>}
      {sub && <div className="nv-sub">{sub}</div>}
    </Wrap>
  );
};
